import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { uuid } from "@ant-design/plots/es/utils";
import { Typography } from "antd";
import { EmailIcon, PhoneIcon, UserAddressIcon, UserIcon } from "assets/icons";
import {
  PATTERNS,
  USER_TYPE,
  WORKSPACE_USER_TYPE,
} from "constants/appConstant";
import { getIn, useFormik } from "formik";
import parsePhoneNumber from "libphonenumber-js";
import { setIsRefresh } from "stores/slices/layout";
import {
  createWorkspace,
  doGetWorkspaceEvent,
  doSwitchWorkspace,
  getWorkspaceInfo,
  updateWorkspace,
} from "stores/slices/workspace";
import { YupValidations } from "utils/validation";
import * as Yup from "yup";

import AppButton from "components/button";
import FormInput from "components/forms/components/formInput";

import { getMe } from "../../../stores/slices/auth";

import styles from "../../../components/forms/components/styles.module.scss";

export const WorkspaceForm = props => {
  const {
    userDetail,
    authInfo,
    setIsOpenWorkspace,
    workspaceSelected,
    setWorkspaceSelected,
    isView,
    onChangeViewFrm,
    listAllSubscriptions,
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [workspaceDetail, setWorkspaceDetail] = useState();
  const [workspaceInputItems, setWorkspaceInputItems] = useState([]);
  const [commentInputItems, setCommentInputItems] = useState([]);
  const [contactInputItems, setContactInputItems] = useState([]);
  const [packageInputItems, setPackageInputItems] = useState([]);
  const [numberPhoneChange, setChangeValueNumber] = useState();

  const validationSchema = Yup.object().shape({
    workspaceName: Yup.string()
      .trim()
      .required("validate.required")
      .matches(PATTERNS.XSS_PATTERN, "validate.noxss"),
    address: YupValidations.requiredString,
    contactName: YupValidations.requiredString,
    contactNumber: Yup.string()
      .trim()
      .required("validate.required")
      .test("contactNumber", "validate.phoneNumber.invalid", value => {
        let contactNumber = value;
        if (
          PATTERNS.PHONE_NUMBER_LENGTH.test(value) &&
          PATTERNS.ZERO_ONLY.test(value)
        ) {
          const validPhoneNumber = parsePhoneNumber(contactNumber);
          if (validPhoneNumber && validPhoneNumber.isPossible()) {
            if (validPhoneNumber.country && validPhoneNumber.isValid()) {
              const newValue = validPhoneNumber.formatInternational();
              setChangeValueNumber(
                newValue.toString().replaceAll(" ", "").replaceAll("+", "")
              );
              return true;
            }
          } else {
            contactNumber = value.includes("+") ? value : `+${contactNumber}`;
            const validContactNumber = parsePhoneNumber(contactNumber);
            if (validContactNumber && validContactNumber.isPossible()) {
              if (validContactNumber.country && validContactNumber.isValid()) {
                const newValue = validContactNumber.formatInternational();
                setChangeValueNumber(
                  newValue.toString().replaceAll(" ", "").replaceAll("+", "")
                );
                return true;
              } else {
                setChangeValueNumber(value);
                return false;
              }
            } else {
              const validPhoneNumber2 = parsePhoneNumber(value, "AU");
              if (validPhoneNumber2 && validPhoneNumber2.isPossible()) {
                if (validPhoneNumber2.country && validPhoneNumber2.isValid()) {
                  const newValue = validPhoneNumber2.formatInternational();
                  setChangeValueNumber(
                    newValue.toString().replaceAll(" ", "").replaceAll("+", "")
                  );
                  return true;
                } else {
                  setChangeValueNumber(value);
                  return false;
                }
              } else {
                const validContactNumber2 = parsePhoneNumber(
                  contactNumber,
                  "AU"
                );
                if (validContactNumber2 && validContactNumber2.isPossible()) {
                  if (
                    validContactNumber2.country &&
                    validContactNumber2.isValid()
                  ) {
                    const newValue = validContactNumber2.formatInternational();
                    setChangeValueNumber(
                      newValue
                        .toString()
                        .replaceAll(" ", "")
                        .replaceAll("+", "")
                    );
                    return true;
                  } else {
                    setChangeValueNumber(value);
                    return false;
                  }
                }
              }
            }
          }
        } else {
          setChangeValueNumber(value);
          return false;
        }
      })
      .matches(PATTERNS.PHONE_NUMBER_LENGTH, {
        message: "validate.phoneNumber.invalid",
      })
      .min(6, "validate.min_length_6")
      .max(20, "validate.max_length_20"),
    contactEmail: YupValidations.email,
    contactInvoices: YupValidations.email,
    comment: Yup.string()
      .trim()
      .max(5000, "validate.max_length_5000")
      .matches(PATTERNS.XSS_PATTERN, {
        message: "validate.noxss",
      }),
  });

  const doListenWorkspace = async workspaceId => {
    if (workspaceId) {
      await dispatch(doGetWorkspaceEvent({ workspaceId: workspaceId }));
    }
  };

  const onSubmit = async data => {
    setLoading(true);

    const request = {
      name: data.workspaceName?.trim(),
      phoneNumber: data.contactNumber?.trim(),
      address: data.address?.trim(),
      contactName: data.contactName?.trim(),
      contactEmail: data.contactEmail,
      invoiceEmail: data.contactInvoices?.trim(),
      currency: "AUD",
    };
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      request.comment = data.comment;
    }
    if (workspaceSelected) {
      if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
        const subscription = listAllSubscriptions.find(
          item =>
            item.name === data.subscriptionPackage ||
            item.id === data.subscriptionPackage
        );
        request.subscriptionPackageId = subscription.id;
      }
      const result = await dispatch(
        updateWorkspace({ ...request, id: workspaceSelected.id })
      );
      if (result && result.payload.status === 200) {
        setIsOpenWorkspace(false);
        dispatch(setIsRefresh(uuid()));
        if (workspaceSelected.id === authInfo.workspaceId) {
          dispatch(getMe());
        }
      }
      setLoading(false);
    } else {
      const result = await dispatch(createWorkspace(request));
      if (result && result.payload.status === 200) {
        if (authInfo.type === USER_TYPE.USER && authInfo.workspaceId === null) {
          const newResult = await dispatch(
            doSwitchWorkspace({
              id: result.payload.data.id,
            })
          );
          if (newResult && newResult.payload.status === 200) {
            dispatch(getMe());
          }
        }
        setIsOpenWorkspace(false);
        dispatch(setIsRefresh(uuid()));
      }
      setLoading(false);
    }
  };
  const handleForm = useFormik({
    initialValues: {
      workspaceName: "",
      address: "",
      contactName: "",
      contactNumber: "",
      contactEmail: "",
      contactInvoices: "",
      comment: "",
      subscriptionPackage: "",
    },
    validationSchema: validationSchema,
    onSubmit: onSubmit,
  });

  const { values, setFieldValue, handleSubmit, handleReset, setValues } =
    handleForm;

  useEffect(() => {
    setFieldValue("contactNumber", numberPhoneChange);
  }, [numberPhoneChange]);

  const handleInputField = () => {
    const workspaceInputItems = [
      {
        type: "text",
        key: "workspaceName",
        label: t("workspace.workspaceName"),
        icon: isView && <UserIcon />,
        placeholder: "workspace.workspaceName",
        span: 24,
        style: {
          marginBottom: 0,
        },
        required: !isView,
        readOnly: isView,
      },
    ];
    const commentInputItems = [
      {
        type: !isView ? "textarea" : "text",
        key: "comment",
        span: 24,
        style: {
          marginBottom: 0,
        },
        config: {
          maxLength: 5000,
          multipleLine: true,
        },
        readOnly: isView,
      },
    ];
    const contactInputItems = [
      {
        type: "text",
        key: "contactName",
        label: t("workspace.contactName"),
        placeholder: "workspace.contactName",
        icon: <UserIcon />,
        span: 24,
        style: {
          marginTop: workspaceSelected ? 10 : 0,
        },
        required: !isView,
        readOnly: isView,
      },
      {
        type: "text",
        key: "contactEmail",
        label: t("workspace.email"),
        placeholder: "workspace.email",
        icon: <EmailIcon />,
        span: 24,
        required: !isView,
        readOnly: isView,
      },
      {
        type: "text",
        key: "contactInvoices",
        label: t("workspace.contactInvoices"),
        placeholder: "workspace.contactInvoices",
        icon: <EmailIcon />,
        span: 24,
        style: {
          marginBottom: 16,
        },
        required: !isView,
        readOnly: isView,
      },
      {
        type: "text",
        key: "contactNumber",
        label: t("workspace.phoneNumber"),
        placeholder: "text.userPhoneNumber",
        icon: <PhoneIcon />,
        span: 24,
        required: !isView,
        readOnly: isView,
      },
      {
        type: !isView ? "textarea" : "text",
        key: "address",
        label: t("workspace.companyAddress"),
        placeholder: "workspace.companyAddress",
        icon: <UserAddressIcon />,
        span: 24,
        style: {
          marginBottom: 0,
        },
        config: {
          multipleLine: true,
        },
        required: !isView,
        readOnly: isView,
      },
    ];
    const packageInputItems = [
      {
        type: isView ? "text" : "select-search",
        key: "subscriptionPackage",
        label: t("workspace.subscriptionPackage"),
        placeholder: t("workspace.subscriptionPackage"),
        options: listAllSubscriptions,
        span: 24,
        style: {
          marginBottom: 0,
        },
        selectKeyValue: "name",
        // config: {
        //   disabled: isView,
        // },
        readOnly: isView,
        noAllowClear: true,
      },
    ];
    setWorkspaceInputItems(workspaceInputItems);
    setCommentInputItems(commentInputItems);
    setContactInputItems(contactInputItems);
    setPackageInputItems(packageInputItems);
  };

  useEffect(() => {
    if (workspaceSelected && workspaceSelected.id) {
      doListenWorkspace(workspaceSelected.id);
      loadWorkspaceDetail().then(res => {
        setWorkspaceDetail(res);
      });
    }
  }, [workspaceSelected]);

  useEffect(() => {
    handleInputField();
  }, [isView]);

  const loadWorkspaceDetail = async () => {
    const { payload } = await dispatch(getWorkspaceInfo(workspaceSelected.id));
    if (payload) {
      return payload;
    }
  };

  const handleChangeContact = (key, value) => {
    if (key !== "contactNumber") {
      setFieldValue(key, value);
    } else {
      if (value.length < 19) {
        setFieldValue(key, value);
      }
    }
  };

  useEffect(() => {
    if (workspaceDetail) {
      setValues({
        workspaceName: workspaceDetail.name || "",
        address: workspaceDetail.address || "",
        contactName: workspaceDetail.contactName || "",
        contactNumber: workspaceDetail.phoneNumber || "",
        contactEmail: workspaceDetail.contactEmail || "",
        contactInvoices: workspaceDetail.invoiceEmail || "",
        comment: workspaceDetail.comment || "",
        subscriptionPackage: isView
          ? workspaceDetail?.subscriptionPackage?.name || ""
          : workspaceDetail?.subscriptionPackage?.id || "",
      });
    } else {
      if (authInfo && authInfo.type !== USER_TYPE.SUPER_ADMIN) {
        setValues({
          contactName:
            userDetail?.firstName || userDetail?.lastName
              ? userDetail.firstName + " " + userDetail.lastName
              : "",
          contactNumber: userDetail?.phoneNumber || "",
          contactEmail: userDetail.email || userDetail?.email || "",
          contactInvoices: userDetail.email || userDetail?.email || "",
          workspaceName: "",
          address: "",
          subscriptionPackage: "",
        });
      }
    }
  }, [workspaceDetail]);

  return (
    <div style={{ height: "100%" }}>
      <div>
        <FormInput
          items={workspaceInputItems.map(item => {
            item.label = t(item.label);
            if (item.key) {
              item.value = getIn(values, item.key);
            }
            return { ...item };
          })}
          form={handleForm}
          onChange={setFieldValue}
          layout={"vertical"}
        />
      </div>
      <div>
        {!isView && !workspaceSelected && (
          <Typography className={styles.labelGroupHeader}>
            {t("workspace.workspaceContactDetails")}
          </Typography>
        )}
        <FormInput
          items={contactInputItems.map(item => {
            item.label = t(item.label);
            if (item.key) {
              item.value = getIn(values, item.key);
            }
            return { ...item };
          })}
          form={handleForm}
          onChange={handleChangeContact}
          layout={!isView && "vertical"}
        />
      </div>
      {authInfo.type === USER_TYPE.SUPER_ADMIN && (
        <>
          {workspaceSelected && (
            <div>
              <Typography className={styles.labelGroupHeader}>
                {t("workspace.workspaceSubscriptionPackage")}
              </Typography>
              <FormInput
                items={packageInputItems.map(item => {
                  item.label = t(item.label);
                  if (item.key) {
                    item.value = getIn(values, item.key);
                  }
                  return { ...item };
                })}
                form={handleForm}
                onChange={setFieldValue}
                layout={"vertical"}
              />
            </div>
          )}

          <div>
            <Typography className={styles.labelGroupHeader}>
              {t("workspace.comment")}
            </Typography>
            <FormInput
              items={commentInputItems.map(item => {
                item.label = t(item.label);
                if (item.key) {
                  item.value = getIn(values, item.key);
                }
                return { ...item };
              })}
              form={handleForm}
              onChange={setFieldValue}
              layout={"vertical"}
            />
          </div>
        </>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          marginTop: "16px",
        }}
      >
        <AppButton
          label={t("label.cancel")}
          color={"grey"}
          loading={loading}
          type="button"
          onClick={() => {
            setIsOpenWorkspace(false);
            handleReset();
            setWorkspaceSelected(null);
          }}
          size={"minimum"}
          className={"cancelButton"}
        />
        {isView ? (
          <AppButton
            label={t("button.edit")}
            color={"blue"}
            loading={loading}
            type="primary"
            disabled={
              authInfo.type === USER_TYPE.USER &&
              workspaceSelected.workspaceUser?.type !==
                WORKSPACE_USER_TYPE.ADMIN
            }
            onClick={() => onChangeViewFrm(false)}
            size={"minimum"}
          />
        ) : (
          <AppButton
            label={t("label.save")}
            color={"blue"}
            loading={loading}
            type="submit"
            onClick={handleSubmit}
            size={"minimum"}
          />
        )}
      </div>
    </div>
  );
};

export default WorkspaceForm;
